import lodashSum from 'lodash.sum';
import { ALL_CHANNELS, Channel, Measure, MetricDataPoint } from 'cio/utils/metrics/common';
import { cumulativeAverageSeries } from 'cio/helpers/cio-math';
import { JourneyMetrics, JourneyMetricsMeasure } from 'cio/utils/journey-metrics';
import { ActionType } from 'cio/models/action';
import type { Metric } from 'cio/utils/metrics/models/metric';
import type { DashboardTestSplitMetric } from 'cio/utils/metrics/models/dashboard-tests';
import type { SplitMetric } from 'cio/utils/metrics/models/split-metric';
import { deliveryStatesAndActionsPerType } from '../action-info';

export enum MeasureType {
  DeliveryMetrics = 'delivery_metrics',
  JourneyMetrics = 'journey_metrics',
}

export interface CalculatedMeasure {
  totals: MetricDataPoint[];
  total: number;
  rates: MetricDataPoint[];
  rate: number | undefined;
  cumulativeAverageRates: MetricDataPoint[];
  allTimeTotal: number;
  allTimeRate: number | undefined;
  rawTotal: number; // the total of all the metric data provided, not trimmed to timerange
  rawRate: number | undefined; // the rate of all metric data provided
}

interface BaseMeasureDefinition<T extends MeasureType> {
  name: T extends MeasureType.DeliveryMetrics ? Measure : JourneyMetricsMeasure;
  type: T;
  label: string;
  valueLabel?: string;
  isComputed: boolean;
  disabledForChannels: Set<Channel>;
  denominator?: T extends MeasureType.DeliveryMetrics ? Measure : JourneyMetricsMeasure;
  isDenominatorConfigurable?: boolean;
  actionTypeDenominators?: Partial<
    Record<ActionType, T extends MeasureType.DeliveryMetrics ? Measure : JourneyMetricsMeasure | null>
  >;
}

interface RawMeasureDefinition<T extends MeasureType> extends BaseMeasureDefinition<T> {
  valueKey: T extends MeasureType.DeliveryMetrics ? Measure : JourneyMetricsMeasure;
  isComputed: false;
  denominator?: T extends MeasureType.DeliveryMetrics ? Measure : JourneyMetricsMeasure;
}

interface ComputedMeasureDefinition<T extends MeasureType> extends BaseMeasureDefinition<T> {
  calculate: (data: Record<string, MetricDataPoint[]>) => MetricDataPoint[];
  isComputed: true;
  dependentKeys: string[];
}

export type MeasureDefinition<T extends MeasureType> = RawMeasureDefinition<T> | ComputedMeasureDefinition<T>;

export enum DeliveryMetric {
  delivered = 'delivered',
  opened = 'opened',
  clicked = 'clicked',
  converted = 'converted',
  bounced = 'bounced',
  suppressed = 'suppressed',
  spammed = 'spammed',
  unsubscribed = 'unsubscribed',
  topicUnsubscribed = 'topic_unsubscribed',
}

export const DELIVERY_METRICS_MEASURE_DEFINITIONS: Record<string, MeasureDefinition<MeasureType.DeliveryMetrics>> = {
  attempted: {
    name: 'attempted',
    type: MeasureType.DeliveryMetrics,
    label: 'Attempted',
    valueKey: 'attempted',
    denominator: 'sent',
    isDenominatorConfigurable: false,
    isComputed: false,
    disabledForChannels: new Set(),
  },
  bounced: {
    name: 'bounced',
    type: MeasureType.DeliveryMetrics,
    label: 'Bounced',
    valueKey: 'bounced',
    denominator: 'sent',
    isDenominatorConfigurable: false,
    isComputed: false,
    disabledForChannels: new Set(),
  },
  clicked: {
    name: 'clicked',
    type: MeasureType.DeliveryMetrics,
    label: 'Clicked',
    valueKey: 'clicked',
    denominator: 'sent',
    isDenominatorConfigurable: true,
    isComputed: false,
    disabledForChannels: new Set(),
    actionTypeDenominators: {
      email_action: 'sent',
      split_randomized_action: 'sent',
    },
  },
  clicked_to_opened: {
    name: 'clicked_to_opened',
    type: MeasureType.DeliveryMetrics,
    label: 'Click-to-open',
    valueLabel: 'Clicked',
    valueKey: 'clicked',
    denominator: 'opened',
    isDenominatorConfigurable: false,
    isComputed: false,
    disabledForChannels: new Set(),
  },
  converted: {
    name: 'converted',
    type: MeasureType.DeliveryMetrics,
    label: 'Converted',
    valueKey: 'converted',
    denominator: 'sent',
    isDenominatorConfigurable: true,
    isComputed: false,
    disabledForChannels: new Set(),
    actionTypeDenominators: {
      email_action: 'sent',
      split_randomized_action: 'sent',
    },
  },
  created: {
    name: 'created',
    type: MeasureType.DeliveryMetrics,
    label: 'Created',
    valueKey: 'created',
    isComputed: false,
    disabledForChannels: new Set(),
  },
  delivered: {
    name: 'delivered',
    type: MeasureType.DeliveryMetrics,
    label: 'Delivered',
    valueKey: 'delivered',
    denominator: 'sent',
    isDenominatorConfigurable: false,
    isComputed: false,
    disabledForChannels: new Set(),
  },
  failed: {
    name: 'failed',
    type: MeasureType.DeliveryMetrics,
    label: 'Failed',
    valueKey: 'failed',
    denominator: 'created',
    isDenominatorConfigurable: false,
    isComputed: false,
    disabledForChannels: new Set(),
  },
  opened: {
    name: 'opened',
    type: MeasureType.DeliveryMetrics,
    label: 'Opened',
    valueKey: 'opened',
    denominator: 'sent',
    isDenominatorConfigurable: true,
    isComputed: false,
    disabledForChannels: new Set(),
    actionTypeDenominators: {
      email_action: 'sent',
      split_randomized_action: 'sent',
    },
  },
  sent: {
    name: 'sent',
    type: MeasureType.DeliveryMetrics,
    label: 'Sent',
    valueKey: 'sent',
    denominator: 'created',
    isDenominatorConfigurable: false,
    isComputed: false,
    disabledForChannels: new Set(),
  },
  spammed: {
    name: 'spammed',
    type: MeasureType.DeliveryMetrics,
    label: 'Marked as spam',
    valueKey: 'spammed',
    denominator: 'sent',
    isDenominatorConfigurable: true,
    isComputed: false,
    disabledForChannels: new Set(),
    actionTypeDenominators: {
      email_action: 'sent',
      split_randomized_action: 'sent',
    },
  },
  suppressed: {
    name: 'suppressed',
    type: MeasureType.DeliveryMetrics,
    label: 'Suppressed',
    valueKey: 'suppressed',
    denominator: 'created',
    isDenominatorConfigurable: false,
    isComputed: false,
    disabledForChannels: new Set(),
  },
  undeliverable: {
    name: 'undeliverable',
    type: MeasureType.DeliveryMetrics,
    label: 'Undeliverable',
    valueKey: 'undeliverable',
    denominator: 'created',
    isDenominatorConfigurable: false,
    isComputed: false,
    disabledForChannels: new Set(),
  },
  unsubscribed: {
    name: 'unsubscribed',
    type: MeasureType.DeliveryMetrics,
    label: 'Unsubscribed',
    valueKey: 'unsubscribed',
    denominator: 'sent',
    isDenominatorConfigurable: true,
    isComputed: false,
    disabledForChannels: new Set(),
    actionTypeDenominators: {
      email_action: 'sent',
      split_randomized_action: 'sent',
    },
  },
  topic_unsubscribed: {
    name: 'topic_unsubscribed',
    type: MeasureType.DeliveryMetrics,
    label: 'Unsubscribed from topics',
    valueKey: 'topic_unsubscribed',
    denominator: 'sent',
    isDenominatorConfigurable: true,
    isComputed: false,
    disabledForChannels: new Set(),
    actionTypeDenominators: {
      email_action: 'sent',
      split_randomized_action: 'sent',
    },
  },
};

export const JOURNEY_METRICS_MEASURE_DEFINITIONS: Record<string, MeasureDefinition<MeasureType.JourneyMetrics>> = {
  started: {
    name: 'started',
    type: MeasureType.JourneyMetrics,
    label: 'Triggered',
    valueKey: 'started',
    isComputed: false,
    disabledForChannels: new Set(),
  },
  activated: {
    name: 'activated',
    type: MeasureType.JourneyMetrics,
    label: 'Started',
    valueKey: 'activated',
    denominator: 'started',
    isComputed: false,
    disabledForChannels: new Set(),
  },
  messaged: {
    name: 'messaged',
    type: MeasureType.JourneyMetrics,
    label: 'Messaged',
    valueKey: 'messaged',
    denominator: 'started',
    isComputed: false,
    disabledForChannels: new Set(),
  },
  finished: {
    name: 'finished',
    type: MeasureType.JourneyMetrics,
    label: 'Completed',
    valueKey: 'finished',
    denominator: 'started',
    isComputed: false,
    disabledForChannels: new Set(),
  },
  exited_early: {
    name: 'exited_early',
    type: MeasureType.JourneyMetrics,
    label: 'Exited Early',
    valueKey: 'exited_early',
    denominator: 'started',
    isComputed: false,
    disabledForChannels: new Set(),
  },
  never_activated: {
    name: 'never_activated',
    type: MeasureType.JourneyMetrics,
    label: 'Filtered Out',
    valueKey: 'never_activated',
    denominator: 'started',
    isComputed: false,
    disabledForChannels: new Set(),
  },
  converted: {
    name: 'converted',
    type: MeasureType.JourneyMetrics,
    label: 'Converted',
    valueKey: 'converted',
    denominator: 'activated',
    isComputed: false,
    disabledForChannels: new Set(),
  },
  in_workflow: {
    name: 'in_workflow',
    type: MeasureType.JourneyMetrics,
    label: 'In Progress',
    denominator: 'started',
    isComputed: true,
    disabledForChannels: new Set(),
    dependentKeys: ['started', 'exited_early', 'never_activated', 'finished'],
    calculate: (data: {
      started: MetricDataPoint[];
      exited_early: MetricDataPoint[];
      never_activated: MetricDataPoint[];
      finished: MetricDataPoint[];
    }) => {
      return data.started.map((started, index) => ({
        x: started.x,
        y:
          (started.y ?? 0) -
          ((data.exited_early[index].y ?? 0) + (data.never_activated[index].y ?? 0) + (data.finished[index].y ?? 0)),
      }));
    },
  },
};

export const MEASURE_DEFINITIONS = {
  [MeasureType.DeliveryMetrics]: DELIVERY_METRICS_MEASURE_DEFINITIONS,
  [MeasureType.JourneyMetrics]: JOURNEY_METRICS_MEASURE_DEFINITIONS,
};

export interface DeliveryMetricsDenominatorEntry {
  denominator: Measure | null;
  actionTypeDenominators?: Partial<Record<ActionType, Measure | null>> | null;
}

export function findMeasureDefinition(
  measure: Measure,
  measureType?: MeasureType.DeliveryMetrics,
): MeasureDefinition<MeasureType.DeliveryMetrics> | null;
export function findMeasureDefinition(
  measure: JourneyMetricsMeasure,
  measureType: MeasureType.JourneyMetrics,
): MeasureDefinition<MeasureType.JourneyMetrics> | null;
export function findMeasureDefinition(
  measure: any,
  measureType: MeasureType = MeasureType.DeliveryMetrics,
): MeasureDefinition<MeasureType> | null {
  return MEASURE_DEFINITIONS[measureType]?.[measure];
}

let cachedDenominators: Record<
  MeasureType,
  Record<Measure, DeliveryMetricsDenominatorEntry> | Record<JourneyMetricsMeasure, JourneyMetricsMeasure | null>
> | null = null;

export function measureDenominators(
  measureType: MeasureType.DeliveryMetrics,
): Record<Measure, DeliveryMetricsDenominatorEntry>;
export function measureDenominators(
  measureType: MeasureType.JourneyMetrics,
): Record<JourneyMetricsMeasure, JourneyMetricsMeasure | null>;
export function measureDenominators(
  measureType: MeasureType,
): Record<Measure, DeliveryMetricsDenominatorEntry> | Record<JourneyMetricsMeasure, JourneyMetricsMeasure | null> {
  if (cachedDenominators?.[measureType]) {
    return cachedDenominators[measureType];
  }

  let denominators = {
    [MeasureType.DeliveryMetrics]: Object.values(DELIVERY_METRICS_MEASURE_DEFINITIONS).reduce<
      Record<Measure, DeliveryMetricsDenominatorEntry>
    >(
      (acc, m) => {
        acc[m.name] = {
          denominator: m.denominator ?? null,
          actionTypeDenominators: m.actionTypeDenominators ?? null,
        };
        return acc;
      },
      {} as Record<Measure, DeliveryMetricsDenominatorEntry>,
    ),
    [MeasureType.JourneyMetrics]: Object.values(JOURNEY_METRICS_MEASURE_DEFINITIONS).reduce<
      Record<JourneyMetricsMeasure, JourneyMetricsMeasure | null>
    >(
      (acc, m) => {
        acc[m.name] = m.denominator ?? null;
        return acc;
      },
      {} as Record<JourneyMetricsMeasure, JourneyMetricsMeasure | null>,
    ),
  };

  cachedDenominators = denominators;

  return denominators[measureType];
}

export function updateConfigurableDenominators(
  denominator: Measure | JourneyMetricsMeasure,
  actionType: ActionType = 'email_action',
) {
  [
    ...Object.values(DELIVERY_METRICS_MEASURE_DEFINITIONS),
    ...Object.values(JOURNEY_METRICS_MEASURE_DEFINITIONS),
  ].forEach((item) => {
    if (!item.isDenominatorConfigurable) {
      return;
    }

    if (!item.actionTypeDenominators) {
      item.actionTypeDenominators = {};
    }

    item.actionTypeDenominators[actionType] = denominator;

    if (actionType === 'email_action') {
      item.actionTypeDenominators.split_randomized_action = denominator;
    }
  });

  cachedDenominators = null;
}

export function updateChannelStateForMeasure(
  measure: Measure | JourneyMetricsMeasure,
  channel: Channel,
  enabled = true,
) {
  let measureDefinition: MeasureDefinition<MeasureType> | null = null;

  if (measure in DELIVERY_METRICS_MEASURE_DEFINITIONS) {
    measureDefinition = DELIVERY_METRICS_MEASURE_DEFINITIONS[measure as Measure];
  } else if (measure in JOURNEY_METRICS_MEASURE_DEFINITIONS) {
    measureDefinition = JOURNEY_METRICS_MEASURE_DEFINITIONS[measure as JourneyMetricsMeasure];
  }

  // this shouldn't ever happen, but just in case and to make TS happy
  if (measureDefinition == null) {
    return;
  }

  if (enabled && measureDefinition.disabledForChannels.has(channel)) {
    measureDefinition.disabledForChannels.delete(channel);
  } else if (!enabled && !measureDefinition.disabledForChannels.has(channel)) {
    measureDefinition.disabledForChannels.add(channel);
  }

  cachedDenominators = null;
}

export function isMeasureEnabledForChannel(
  type: MeasureType,
  measure: Measure | JourneyMetricsMeasure,
  channel: Channel,
): boolean {
  return !MEASURE_DEFINITIONS[type]?.[measure]?.disabledForChannels.has(channel) ?? true;
}

// Convenience functions
export function isDeliveryMeasureEnabledForChannel(measure: Measure, channel: Channel) {
  return isMeasureEnabledForChannel(MeasureType.DeliveryMetrics, measure, channel);
}
export function isDeliveryMeasureEnabledForActionType(measure: Measure, actionType: ActionType) {
  let channel = actionType.replace('_action', '');

  // don't create false negatives when the actionType doesn't represent a channel
  if (!ALL_CHANNELS.includes(channel as any)) {
    return true;
  }

  return isMeasureEnabledForChannel(MeasureType.DeliveryMetrics, measure, channel as Channel);
}
export function isJourneyMeasureEnabledForChannel(measure: JourneyMetricsMeasure, channel: Channel) {
  return isMeasureEnabledForChannel(MeasureType.JourneyMetrics, measure, channel);
}
export function isJourneyMeasureEnabledForActionType(measure: Measure, actionType: ActionType) {
  let channel = actionType.replace('_action', '');

  // don't create false negatives when the actionType doesn't represent a channel
  if (!ALL_CHANNELS.includes(channel as any)) {
    return true;
  }

  return isMeasureEnabledForChannel(MeasureType.JourneyMetrics, measure, channel as Channel);
}

function availableMeasuresForMetricActionType(m: Metric | DashboardTestSplitMetric | SplitMetric) {
  let canConvert = m.canConvert ?? false;
  let actionType = m.actionType as ActionType | null;

  if (actionType != null) {
    return ((deliveryStatesAndActionsPerType(canConvert)[actionType] ?? []) as Measure[]).filter((measure) =>
      isDeliveryMeasureEnabledForActionType(measure, actionType as ActionType),
    );
  }

  return [];
}

export function findNumerator(measure: JourneyMetricsMeasure, type: MeasureType.JourneyMetrics): JourneyMetricsMeasure;
export function findNumerator(measure: Measure, type?: MeasureType.DeliveryMetrics): Measure;
export function findNumerator(measure: any, type: any = MeasureType.DeliveryMetrics): any {
  return (MEASURE_DEFINITIONS as any)[type]?.[measure]?.valueKey;
}

export function findDenominator(
  measure: JourneyMetricsMeasure,
  type: MeasureType.JourneyMetrics,
): JourneyMetricsMeasure;
export function findDenominator(measure: Measure, type?: MeasureType.DeliveryMetrics, actionType?: ActionType): Measure;
export function findDenominator(measure: any, type: any = MeasureType.DeliveryMetrics, actionType: any = null): any {
  let measureDefinition = (MEASURE_DEFINITIONS as any)[type][measure];

  if (actionType != null && measureDefinition?.actionTypeDenominators?.[actionType]) {
    return measureDefinition?.actionTypeDenominators?.[actionType];
  }

  return measureDefinition.denominator;
}

export function calculateMeasure(
  measure: Measure | JourneyMetricsMeasure,
  type: MeasureType,
  metrics: (Metric | JourneyMetrics | DashboardTestSplitMetric | SplitMetric)[],
  rawData: Record<Measure | JourneyMetricsMeasure, MetricDataPoint[]>,
  groupedData: Record<Measure | JourneyMetricsMeasure, MetricDataPoint[]>,
  allTimeData?: Record<Measure | JourneyMetricsMeasure, number>,
): CalculatedMeasure {
  let measureDefinition = MEASURE_DEFINITIONS[type][measure];

  if (type === MeasureType.DeliveryMetrics) {
    let actionTypes = metrics.map((m: Metric | DashboardTestSplitMetric | SplitMetric) => m.actionType);

    if (
      actionTypes.every(
        (actionType) => !isDeliveryMeasureEnabledForActionType(measure as Measure, (actionType ?? '') as any),
      )
    ) {
      return {
        totals: [],
        total: 0,
        rate: undefined,
        rates: [],
        cumulativeAverageRates: [],
        allTimeTotal: 0,
        allTimeRate: undefined,
        rawTotal: 0,
        rawRate: undefined,
      };
    }
  }

  if (type === MeasureType.DeliveryMetrics && measure !== 'created') {
    let actionTypes = metrics.map((m) => (m as Metric | DashboardTestSplitMetric | SplitMetric).actionType);
    let canConvert = metrics.some((m) => (m as Metric | DashboardTestSplitMetric | SplitMetric).canConvert);
    let availableMeasures = actionTypes.reduce<Measure[]>((acc, type: ActionType) => {
      let _type = type === 'split_randomized_action' ? 'email_action' : type;
      let typeMeasures = ((deliveryStatesAndActionsPerType(canConvert)[_type] ?? []) as Measure[]).filter((measure) =>
        isDeliveryMeasureEnabledForActionType(measure, _type),
      );
      acc = [...acc, ...typeMeasures];
      return acc;
    }, []);

    if (!availableMeasures.includes(measure as Measure)) {
      return {
        totals: [],
        total: 0,
        rate: undefined,
        rates: [],
        cumulativeAverageRates: [],
        allTimeTotal: 0,
        allTimeRate: undefined,
        rawTotal: 0,
        rawRate: undefined,
      };
    }
  }

  let rawValues: MetricDataPoint[] = [];
  let groupedValues: MetricDataPoint[] = [];
  if (measureDefinition.isComputed) {
    rawValues = measureDefinition.calculate(rawData);
    groupedValues = measureDefinition.calculate(groupedData) ?? [];
  } else {
    rawValues = rawData[measureDefinition.valueKey];
    groupedValues = groupedData[measureDefinition.valueKey] ?? [];
  }

  let rawTotal = lodashSum(rawValues.map((p) => p.y)) ?? 0;
  let rawRate = undefined;

  let total = lodashSum(groupedValues.map((p) => p.y)) ?? 0;
  let rate = undefined;

  let allTimeTotal = 0;
  let allTimeRate = undefined;

  let rates: MetricDataPoint[] = [];

  let cumulativeAverageRates: MetricDataPoint[] = [];

  let denominators = [
    ...new Set(
      metrics
        .map((m) =>
          findDenominator(
            measure as any,
            type as any,
            m instanceof JourneyMetrics ? undefined : (m.actionType as ActionType),
          ),
        )
        .filter((item) => item !== undefined && item !== null),
    ),
  ];

  if (denominators.length > 0) {
    let rawDenominatorValues = metrics.reduce<MetricDataPoint[]>((acc, m) => {
      if (!(m instanceof JourneyMetrics) && m.actionType != null) {
        // If the current action type doesn't track the current measure, don't
        // add its values to denominators
        let availableMeasuresForActionType = availableMeasuresForMetricActionType(m);
        if (!availableMeasuresForActionType.includes(measure as Measure)) {
          return acc;
        }
      }

      let denominator = findDenominator(
        measure as any,
        type as any,
        m instanceof JourneyMetrics ? undefined : (m.actionType as ActionType),
      );

      if (denominator == null) {
        return acc;
      }

      ((m.rawMetrics as Record<any, MetricDataPoint[]>)[denominator] ?? []).forEach((v, i) => {
        if (acc[i] == null) {
          acc[i] = v;
        } else {
          acc[i] = {
            x: acc[i].x,
            y: (acc[i].y ?? 0) + (v.y ?? 0),
          };
        }
      });

      return acc;
    }, []);

    if (rawDenominatorValues != null) {
      let groupedDenominatorValues = metrics.reduce<MetricDataPoint[]>((acc, m) => {
        if (!(m instanceof JourneyMetrics) && m.actionType != null) {
          // If the current action type doesn't track the current measure, don't
          // add its values to denominators
          let availableMeasuresForActionType = availableMeasuresForMetricActionType(m);
          if (!availableMeasuresForActionType.includes(measure as Measure)) {
            return acc;
          }
        }

        let denominator = findDenominator(
          measure as any,
          type as any,
          m instanceof JourneyMetrics ? undefined : (m.actionType as ActionType),
        );

        if (denominator == null) {
          return acc;
        }

        (m.groupedMetrics[denominator] ?? []).forEach((v, i) => {
          if (acc[i] == null) {
            acc[i] = v;
          } else {
            acc[i] = {
              x: acc[i].x,
              y: (acc[i].y ?? 0) + (v.y ?? 0),
            };
          }
        });

        return acc;
      }, []);

      let rawDenominatorTotal = lodashSum(rawDenominatorValues.map((p) => p.y ?? 0));
      let denominatorTotal = lodashSum(groupedDenominatorValues.map((p) => p.y ?? 0));

      rawRate = rawDenominatorTotal === 0 ? undefined : rawTotal / rawDenominatorTotal;
      rate = denominatorTotal === 0 ? undefined : total / denominatorTotal;

      if (groupedValues.length === 0 || groupedDenominatorValues.length === 0) {
        cumulativeAverageRates = [];
      } else {
        // Calculate cumulative average rates
        let avgSeries = cumulativeAverageSeries(
          groupedValues.map((p) => p.y),
          groupedDenominatorValues.map((p) => p.y),
        ) as number[];
        cumulativeAverageRates = avgSeries.map((val, i) => ({ x: groupedValues[i].x, y: val }));

        rates = groupedDenominatorValues.map((p, i) => {
          if (p.y === 0 || p.y == null) {
            return {
              x: p.x,
              y: null,
            };
          }

          return {
            x: p.x,
            y: (groupedValues[i].y ?? 0) / p.y,
          };
        });
      }
    }
  }

  if (!measureDefinition.isComputed) {
    if (allTimeData?.[measureDefinition.valueKey] != null) {
      allTimeTotal = allTimeData[measureDefinition.valueKey];
      if (denominators.length > 0) {
        let allTimeDenominatorTotal = metrics.reduce<number>((acc, m) => {
          let denominator = findDenominator(
            measure as any,
            type as any,
            m instanceof JourneyMetrics ? undefined : (m.actionType as ActionType),
          );

          if (denominator == null) {
            return acc;
          }

          return acc + ((m as any).allTimeTotals[denominator] ?? 0);
        }, 0);

        if (allTimeDenominatorTotal !== 0) {
          allTimeRate = allTimeTotal / allTimeDenominatorTotal;
        }
      }
    }
  }

  return {
    totals: groupedValues,
    total,
    rate,
    rates,
    cumulativeAverageRates,
    allTimeTotal,
    allTimeRate,
    rawTotal,
    rawRate,
  };
}
